<template>
  <nav class="navbar">
    <!-- LEFT -->
    <div class="left-section">
      <div class="brand" @click="$router.push('/')">
        <img src="@/assets/Mudra-logo.png" class="logo" />
        <span class="brand-name">Mudra</span>
      </div>
    </div>

    <!-- CENTER -->
    <ul class="center-menu" :class="{ open: menuOpen }">
      <li><router-link to="/" @click="closeMenu">Home</router-link></li>
      <li><router-link to="/stories" @click="closeMenu">Stories</router-link></li>
      <li><router-link to="/trending" @click="closeMenu">Trending</router-link></li>
      <li v-if="user"><router-link to="/write" @click="closeMenu">Write</router-link></li>
      <li><router-link to="/whitepaper" @click="closeMenu">Whitepaper</router-link></li>
      <li><router-link to="/about" @click="closeMenu">About</router-link></li>
      <li v-if="isAdmin"><router-link to="/admin" @click="closeMenu">Admin</router-link></li>
      <li v-if="isAdmin"><router-link to="/ads-admin" @click="closeMenu">Ad Manager</router-link></li>
    </ul>

    <!-- RIGHT -->
<div class="right-section">

  <!-- 💠 TOKEN BADGE -->
  <div v-if="user" class="token-badge">
    💠 {{ Number(tokens || 0).toFixed(2) }}
  </div>

  <!-- ✉️ MESSAGE ICON (🔥 ADD HERE) -->
  <div v-if="user" class="nav-icon" @click="goToChats">
    ✉️
    <span v-if="messageCount > 0" class="badge dot-only"></span>
  </div>

  <!-- 🔔 NOTIFICATIONS -->
  <div v-if="user" class="notif-wrapper" @click.stop="toggleNotif">
    🔔
    <span v-if="unreadCount > 0" class="notif-count">
      {{ unreadCount }}
    </span>

    <div v-if="notifOpen" class="notif-dropdown">
      <h4>Notifications</h4>

      <p v-if="notifications.length === 0" class="empty-msg">
        No new notifications
      </p>

  <div
    v-for="(n, index) in notifications"
    :key="n.id"
    class="notif-row"
    @click.stop="openNotification(n, index)"
  >
    <span class="msg">
      <strong v-if="n.sender_name">{{ n.sender_name }}</strong>
      {{ n.message }}
    </span>
          </div>
        </div>
      </div>

      <!-- 👤 PROFILE -->
      <div v-if="user" class="profile-wrapper" @click="goMyProfile">
        <img src="@/assets/profile-icon.jpg" class="profile-icon" />
      </div>

      <!-- 🚪 AUTH BUTTONS -->
      <div v-else class="auth-buttons">
        <router-link to="/login" class="btn login-btn">Login</router-link>
        <router-link to="/register" class="btn register-btn">Register</router-link>
      </div>
    </div>
  </nav>

  <!-- 📱 MOBILE BOTTOM NAV -->
  <div class="bottom-nav">
  <router-link to="/" class="nav-item" exact>
    <i class="fa-solid fa-house"></i>
    <span>Home</span>
  </router-link>

  <router-link to="/stories" class="nav-item">
    <i class="fa-solid fa-book-open"></i>
    <span>Stories</span>
  </router-link>

  <router-link to="/trending" class="nav-item">
    <i class="fa-solid fa-fire"></i>
    <span>Trending</span>
  </router-link>

  <!-- ✅ Whitepaper Added -->
  <router-link to="/whitepaper" class="nav-item">
    <i class="fa-solid fa-file-lines"></i>
    <span>Whitepaper</span>
  </router-link>

  <router-link to="/write" class="nav-item">
    <i class="fa-solid fa-pen-nib"></i>
    <span>Write</span>
  </router-link>

  <div class="nav-item" @click="goMyProfile">
  <i class="fa-solid fa-user"></i>
  <span>Profile</span>
</div>
</div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { storeToRefs } from "pinia";
import { useUserStore } from "@/stores/user";

const router = useRouter();
const userStore = useUserStore();

// Destructure from store for easy template usage
const { user, isAdmin, tokens, notifications, unreadCount, messageCount } = storeToRefs(userStore);

const menuOpen = ref(false);
const notifOpen = ref(false);

function goMyProfile() {
  if (user.value) {
    router.replace(`/profile/${user.value.id}`);
  }
}

function closeMenu() { menuOpen.value = false; }
function toggleNotif() { notifOpen.value = !notifOpen.value; }

function handleClickOutside(e) {
  const notifWrapper = document.querySelector(".notif-wrapper");
  if (notifWrapper && !notifWrapper.contains(e.target)) {
    notifOpen.value = false;
  }
}

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
});

async function openNotification(n) {
  await userStore.markNotificationRead(n.id);

  if (n.type === "connection_request") {
    router.push("/requests");
  } else if (n.type === "connection_accept") {
    router.push(`/profile/${n.sender_id}`);
  } else if (n.story_id) {
    router.push(`/story/${n.story_id}`);
  } else if (n.target_user_id) {
    router.push(`/profile/${n.target_user_id}`);
  }
}

function goToChats() {
  router.push("/chats");
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=Orbitron:wght@600&display=swap');
@import url("https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.6.0/css/all.min.css");

/* ===== Navbar Base ===== */
.navbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 64px;
  background: rgba(8, 12, 20, 0.85);
  backdrop-filter: blur(14px);
  border-bottom: 1px solid rgba(0, 255, 255, 0.05);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 28px;
  z-index: 1000;
  font-family: "Inter", sans-serif;
}

/* ===== Left Section ===== */
.brand {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}
.logo {
  width: 34px;
  height: 34px;
  border-radius: 8px;
}
.brand-name {
  font-family: "Orbitron";
  color: #00c8ff;
  font-size: 18px;
  font-weight: 700;
  text-shadow: 0 0 12px rgba(0, 200, 255, 0.4);
}

/* ===== Center Menu ===== */
.center-menu {
  flex: 1;
  display: flex;
  justify-content: center;
  gap: 26px;
  list-style: none;
}
.center-menu a {
  color: #d7eaff;
  font-size: 15px;
  text-decoration: none;
  transition: 0.25s ease;
}
.center-menu a:hover {
  color: #00e5ff;
}

/* ===== Right Section ===== */
.right-section {
  display: flex;
  align-items: center;
  gap: 14px;
}

/* 💠 Token */
.token-badge {
  background: rgba(0, 200, 255, 0.1);
  border: 1px solid rgba(0, 200, 255, 0.3);
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 13px;
  color: #aee8ff;
}

/* 👤 Profile */
.profile-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid rgba(0, 200, 255, 0.3);
  cursor: pointer;
  transition: 0.25s ease;
}
.profile-icon:hover {
  box-shadow: 0 0 10px rgba(0, 200, 255, 0.5);
  transform: scale(1.05);
}

/* 🔥 NAV ICON (MESSAGE) */
.nav-icon {
  position: relative;
  margin: 0 10px;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
}

/* 🔥 MESSAGE COUNT */
.badge {
  position: absolute;
  top: -6px;
  right: -8px;

  background: red;
  color: white;

  font-size: 11px;
  font-weight: bold;

  padding: 2px 6px;
  border-radius: 50%;

  min-width: 18px;
  text-align: center;
}

.badge.dot-only {
  width: 10px;
  height: 10px;
  min-width: 10px;
  padding: 0;
  top: -2px;
  right: -2px;
}



/* 🔔 Notifications */
.notif-wrapper {
  position: relative;
  font-size: 20px;
  color: #e8f7ff;
  cursor: pointer;
}
.notif-count {
  position: absolute;
  top: -4px;
  right: -6px;
  background: #ff4b4b;
  color: white;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 50%;
}
.notif-dropdown {
  position: absolute;
  top: 35px;
  right: 0;
  background: rgba(10, 15, 25, 0.95);
  border: 1px solid rgba(0, 200, 255, 0.3);
  border-radius: 8px;
  padding: 10px;
  width: 250px;
  box-shadow: 0 0 12px rgba(0, 200, 255, 0.2);
  z-index: 100;
}
.notif-dropdown h4 {
  color: #00c8ff;
  margin-bottom: 6px;
  font-size: 14px;
}
.notif-row {
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  padding: 6px 4px;
  cursor: pointer;
}
.notif-row:hover {
  background: rgba(0, 200, 255, 0.08);
}
.notif-row.unread {
  border-left: 3px solid #00c8ff;
}
.msg {
  color: #eaf6ff;
  font-size: 13px;
}
.time {
  color: #9ac7df;
  font-size: 11px;
}
.empty-msg {
  text-align: center;
  color: #bdeaff;
  font-size: 13px;
}

/* 🚪 Auth Buttons */
.auth-buttons {
  display: flex;
  gap: 8px;
}
.btn {
  padding: 6px 14px;
  border-radius: 8px;
  font-weight: 600;
  text-decoration: none;
  font-size: 13px;
  transition: 0.3s;
}
.login-btn {
  background: rgba(255, 255, 255, 0.1);
  color: #00c8ff;
  border: 1px solid rgba(0, 200, 255, 0.4);
}
.register-btn {
  background: linear-gradient(90deg, #00c8ff, #007bff);
  color: black;
}
.login-btn:hover,
.register-btn:hover {
  transform: scale(1.05);
}

/* 📱 Mobile Navbar + Notification FIX */
@media (max-width: 900px) {

.center-menu {
  display: none !important;
}

/* Profile icon hide (mobile) */
.profile-wrapper {
  display: none !important;
}

/* Token badge stays */
.token-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  margin-right: 6px;
}

/* 🔔 Notification Wrapper fix */
.notif-wrapper {
  position: relative;
}

/* ✅ PERFECT mobile notification dropdown */
.notif-dropdown {
  position: fixed !important;
  top: 70px;                 /* 🔑 navbar ke just niche */
  left: 50% !important;
  transform: translateX(-50%);
  width: 92vw !important;
  max-width: 360px;
  max-height: 65vh;
  overflow-y: auto;

  background: rgba(8, 14, 25, 0.96);
  backdrop-filter: blur(16px);
  border-radius: 14px;

  padding: 12px 0;
  z-index: 3000;

  border: 1px solid rgba(0, 200, 255, 0.35);
  box-shadow:
    0 10px 30px rgba(0, 0, 0, 0.55),
    0 0 20px rgba(0, 200, 255, 0.25);
}

/* Notification title */
.notif-dropdown h4 {
  text-align: center;
  margin-bottom: 10px;
  color: #00c8ff;
  font-size: 15px;
  font-weight: 600;
}

/* Each notification row */
.notif-row {
  padding: 10px 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  font-size: 13px;
  line-height: 1.4;
  cursor: pointer;
}

.notif-row:last-child {
  border-bottom: none;
}

.notif-row:hover {
  background: rgba(0, 200, 255, 0.12);
}

.notif-row .msg {
  color: #eaf6ff;
  display: block;
}

.notif-row strong {
  color: #00eaff;
  font-weight: 600;
}

/* Empty state */
.empty-msg {
  text-align: center;
  color: #9ecfff;
  font-size: 13px;
  padding: 12px;
}

/* Bottom navigation */
.bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 65px;
  background: rgba(6, 10, 18, 0.95);
  backdrop-filter: blur(12px);
  display: flex;
  justify-content: space-around;
  align-items: center;
  z-index: 2500;
  border-top: 1px solid rgba(0, 200, 255, 0.3);
}

.nav-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #cfe6ff;
  font-size: 11px;
  text-decoration: none;
  transition: 0.3s;
}

.nav-item i {
  font-size: 20px;
  margin-bottom: 3px;
}

.nav-item.router-link-active {
  color: #00c8ff;
  text-shadow: 0 0 8px rgba(0, 200, 255, 0.7);
}

/* Prevent bottom nav overlap */
body {
  padding-bottom: 75px;
}
}

/* ===============================
   MOBILE BOTTOM NAV FIX
================================ */

@media (max-width: 768px) {
  .bottom-nav {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 4px;
  }

  .bottom-nav .nav-item {
    flex: 1;
    min-width: 0;
    text-align: center;
    font-size: 10px;
    padding: 6px 0;
  }

  .bottom-nav .nav-item i {
    font-size: 16px;
    display: block;
    margin-bottom: 2px;
  }

  .bottom-nav .nav-item span {
    font-size: 10px;
    white-space: nowrap;
  }
}
</style>